import { DaysInput, ResourceSelectInfo, TimeRange } from './../../interfaces/booking';
import ResourceSelect from '@/app/modules/bookings/components/resource-select/resource-select.vue';
import EventConflicts from '@/app/modules/bookings/components/event-conflicts/event-conflicts.vue';
import AutoFormGroup from '@shared/components/forms/auto-form/auto-form-group/auto-form-group.vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Booking, DateRange } from '@bookings/interfaces/booking';
import VuexFormProvider from '@shared/components/forms/auto-form/vuex-form-provider/vuex-form-provider.vue';
import StdxForm from '@shared/components/forms/auto-form/stdx-form/stdx-form.vue';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { BookingEvent } from '@bookings/interfaces/booking-event';
import { Resource } from '@centers/interfaces/resource';
import { Article } from '@accounting/interfaces/article';
import { PriceCategory } from '@contacts/interfaces/price-category';
import { setProp } from '@shared/helpers/operators';
import { Contact } from '@contacts/interfaces/contact';
import MultiplayerBookingSection from '@bookings/components/booking-multiplayer/multiplayer-booking-section.vue';
import { Discipline } from '@centers/interfaces/discipline';
import { MultiplayerBooking } from '@bookings/interfaces/multiplayer-booking';

@Component({
  name: 'bpc-booking-form',
  components: {
    'bpc-stdx-form': StdxForm,
    'bpc-auto-form-group': AutoFormGroup,
    'bpc-event-conflicts': EventConflicts,
    'bpc-resource-select': ResourceSelect,
    'bpc-multiplayer-booking-section': MultiplayerBookingSection,
  },
})
export default class BookingForm extends Vue {
  $refs: {
    // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
    stdForm: InstanceType<typeof StdxForm> & {
      saveEntity(): Promise<BPCGenericApiResponse>;
      formModel: Booking;
      onDeleteMediaFile(): void;
      searchEntity(): Promise<BPCGenericApiResponse>;
      extraData: string | AnyEntity;
      formProvider: VuexFormProvider;
    };
  };
  @Prop(String) readonly entity: string;
  @Prop([String, Number]) readonly entityId: string | number;
  @Prop({ type: String, default: 'default' }) formConfigName: string;
  @Prop({ type: String, default: 'booking' }) storeName: string;
  @Prop({ type: Number, default: 0 }) readonly deep: number;
  @Prop({ type: String, default: 'api' }) readonly mode: string;
  @Prop({ type: Boolean, default: false }) readonly withDraft: boolean;
  @Prop({ type: Boolean, default: false }) readonly disabled: boolean;
  @Prop([String, Object, Array]) readonly extraData: string | AnyEntity;

  public multiplayerBookingData: MultiplayerBooking = {
    players: [],
    number_of_players: null,
  };

  get currentAction(): string {
    return this.$store.getters[`${this.storeName}/currentAction`];
  }
  // date, times, resources, contact, discipline or additional_articles
  get dateRange(): DateRange {
    return this.$store.getters[`${this.storeName}/dateRange`];
  }
  get timeRange(): DateRange {
    return this.$store.getters[`${this.storeName}/timeRange`];
  }
  get startDate(): string {
    return this.$store.getters[`${this.storeName}/bookingStartDate`];
  }
  get startTime(): string {
    return this.$store.getters[`${this.storeName}/bookingStartTime`];
  }
  get endDate(): string {
    return this.$store.getters[`${this.storeName}/bookingEndDate`];
  }
  get endTime(): string {
    return this.$store.getters[`${this.storeName}/bookingEndTime`];
  }

  get resourceIds(): number[] {
    return this.$store.getters[`${this.storeName}/resourceIds`];
  }

  get resources(): Resource[] {
    return this.$store.getters[`${this.storeName}/resources`];
  }

  get bookingEvents(): BookingEvent[] {
    return this.$store.getters[`${this.storeName}/bookingEvents`];
  }

  get contact(): Contact {
    return this.$store.getters[`${this.storeName}/contact`];
  }

  get contactId(): number {
    return this.$store.getters[`${this.storeName}/contactId`];
  }

  get contactPriceCategory(): PriceCategory {
    return this.$store.getters[`${this.storeName}/contactPriceCategory`];
  }

  get priceCategoryId(): number {
    return this.$store.getters[`${this.storeName}/priceCategoryId`];
  }

  get article(): Article {
    return this.$store.getters[`${this.storeName}/article`];
  }
  get articleId(): number {
    return this.$store.getters[`${this.storeName}/articleId`];
  }
  get disciplineId(): number {
    return this.$store.getters[`${this.storeName}/disciplineId`];
  }
  get discipline(): Discipline {
    return this.$store.getters[`${this.storeName}/discipline`];
  }
  get selectedSuggestedArticles(): Record<string, string[]> {
    return this.$store.getters[`${this.storeName}/selectedSuggestedArticles`];
  }
  get isSimpleBooking(): boolean {
    return this.$store.getters[`${this.storeName}/isSimpleBooking`];
  }
  get hasConflicts(): boolean {
    return this.$store.getters[`${this.storeName}/hasConflicts`];
  }

  get isMultiplayerAllowed(): boolean {
    return this.$store.getters[`${this.storeName}/isMultiplayerAllowed`];
  }

  get conflicts(): BookingEvent[] {
    return this.$store.getters[`${this.storeName}/conflicts`];
  }

  get showConflicts(): boolean {
    return this.$store.getters[`${this.storeName}/showConflicts`];
  }

  get resourcesSelectInfo(): ResourceSelectInfo[] {
    return this.$store.getters[`${this.storeName}/resourcesSelectInfo`];
  }

  get type(): ResourceSelectInfo[] {
    return this.$store.getters[`${this.storeName}/type`];
  }

  get days(): DaysInput {
    return this.$store.getters[`${this.storeName}/days`];
  }
  get onlineBooking(): DaysInput {
    return this.$store.getters[`${this.storeName}/onlineBooking`];
  }

  get cantSaveBooking(): boolean {
    return this.$store.getters[`${this.storeName}/cantSaveBooking`];
  }

  get cantSaveBookingMessages(): string[] {
    return this.$store.getters[`${this.storeName}/cantSaveBookingMessages`];
  }

  get cantSearchBooking(): boolean {
    return this.$store.getters[`${this.storeName}/cantSearchBooking`];
  }

  get cantSearchBookingMessages(): string[] {
    return this.$store.getters[`${this.storeName}/cantSearchBookingMessages`];
  }

  get repetitive(): boolean {
    return this.$store.getters[`${this.storeName}/repetitive`];
  }

  get reductionArticles(): Article[] {
    return this.$store.getters[`${this.storeName}/reductionArticles`];
  }

  get daysStr(): string {
    return Object.keys(this.days)
      .filter(dayName => this.days[dayName])
      .join(',');
  }

  get fullExtraData() {
    return { ...this.extraData, action: this.currentAction, contact_id: this.contactId };
  }

  get contactSubscriptions() {
    return this.$store.getters[`${this.storeName}/contactSubscriptions`] || {};
  }

  get contactSubscriptionsStr(): string {
    return JSON.stringify(this.contactSubscriptions);
  }

  public created() {
    console.log('Form Created');
  }

  public mounted() {
    this.$store.commit(`${this.storeName}/setMode`, this.mode);

    this.$root.$on('bookingEventChanged', () => {
      this.calculatePriceIfNothingChange();
    });

    this.$root.$on('resetRelatedFieldsOfGenerateOrderInBookingForm', () => {
      this.resetFieldsRelatedWithGenerationOfOrder();
    });
  }

  public resetFieldsRelatedWithGenerationOfOrder() {
    setProp(this.$refs.stdForm.formModel, 'article_id', null);
    setProp(this.$refs.stdForm.formModel, 'article', null);
    setProp(this.$refs.stdForm.formModel, 'selected_suggested_articles', null);
    setProp(this.$refs.stdForm.formModel, 'price_category_id', null);
    setProp(this.$refs.stdForm.formModel, 'price_category', null);
    setProp(this.$refs.stdForm.formModel, 'reduction_articles', null);
    setProp(this.$refs.stdForm.formModel, 'booking_price', null);
  }

  public resetMultiplayerDataIfNedded() {
    if (!this.isMultiplayerAllowed) {
      this.multiplayerBookingData = {
        number_of_players: null,
        players: [],
      };
    }
  }

  public setWeekDay() {
    if (this.repetitive && this.startDate) {
      this.$store.commit(`${this.storeName}/setWeekDay`, this.startDate);
    }
  }

  public showResourceSelect = false;
  public showCantSaveBookingMessage = false;
  public showCantSearchBookingMessage = false;

  public i: number = 1;

  public async destroyed() {
    await this.$store.dispatch(`${this.storeName}/resetForm`);
    this.$root.$off('bookingEventChanged');
    this.$root.$off('resetRelatedFieldsOfGenerateOrderInBookingForm');
  }

  private getPriceParamsStringId() {
    return [
      this.type,
      this.startTime,
      this.endTime,
      this.startDate,
      this.endDate,
      this.resourceIds.join(','),
      this.disciplineId,
      this.priceCategoryId,
      this.articleId,
      this.onlineBooking,
      // JSON.stringify(this.selectedSuggestedArticles),
    ].join(',');
  }

  calculatePriceIfNothingChange() {
    if (this.$store.getters[`${this.storeName}/canGetPrice`]) {
      const oldRelatedPriceParamsStr = this.getPriceParamsStringId();
      setTimeout(() => {
        const newRelatedPriceParamsStr = this.getPriceParamsStringId();
        if (newRelatedPriceParamsStr === oldRelatedPriceParamsStr) {
          this.$store.dispatch(`${this.storeName}/calcPrice`);
        }
      }, 600);
    }
  }

  changePriceCategory() {
    const newPriceCategory = this.contactPriceCategory;

    if (newPriceCategory) {
      this.$store.commit(`${this.storeName}/setModelProp`, {
        prop: 'price_category',
        value: newPriceCategory,
      });

      this.$store.commit(`${this.storeName}/setModelProp`, {
        prop: 'price_category_id',
        value: newPriceCategory.id,
      });
    } else {
      this.$store.commit(`${this.storeName}/setModelProp`, {
        prop: 'price_category',
        value: null,
      });

      this.$store.commit(`${this.storeName}/setModelProp`, {
        prop: 'price_category_id',
        value: null,
      });
    }
  }

  private changeApplyFreeOrders() {
    const contact = this.contact;
    this.$set(
      this.$refs.stdForm.formModel,
      'apply_free_orders',
      contact?.apply_free_orders ?? false,
    );
  }

  onFormSaved(e) {
    this.$emit('entity-saved', e);
  }

  onSavingEntity(event) {
    this.$emit('saving-entity', event);
  }

  async onConflictChange() {
    this.$store.commit(`${this.storeName}/setShowReplaceButton`, false);
    this.$store.commit(`${this.storeName}/setShowConflicts`, false);
    return await this.searchAndManageConflicts();
  }

  async clickOnReplaceButton(): Promise<string> {
    // Send booking with events to be everwriten
    // const override = await this.$store.dispatch(`${this.storeName}/saveAndOverride'); // TODO implement this method in vuex

    const res = await this.$refs.stdForm.saveEntity();
    this.$store.commit(`${this.storeName}/setShowReplaceButton`, false);
    this.$store.commit(`${this.storeName}/setShowConflicts`, false);
    this.$root.$emit('bookingSaved', res);
    return 'replaced';
  }

  async clickOnSaveButton(): Promise<string> {
    console.log('clickOnSaveButton');
    if (this.cantSaveBooking) {
      console.log('cantSaveBooking');
      console.log('messages', this.cantSaveBookingMessages);
      this.showCantSaveBookingMessage = true;
      return null;
    }
    this.showCantSaveBookingMessage = false;

    this.$store.commit(`${this.storeName}/setShowConflicts`, false);
    const pass = await this.searchAndManageConflicts();
    if (!pass) {
      return null;
    }
    const res = await this.$refs.stdForm.saveEntity();
    this.$root.$emit('bookingSaved', res);

    return 'saved';
  }

  async searchAndManageConflicts(): Promise<boolean> {
    if (this.isSimpleBooking) {
      await this.$refs.stdForm.searchEntity();
      if (this.hasConflicts) {
        // show Conflicts
        this.$store.commit(`${this.storeName}/setShowConflicts`, true);
        this.$store.commit(`${this.storeName}/setShowReplaceButton`, true);
        return false;
      }
    }
    return true;
  }
  // async saveEntity(): Promise<BPCGenericApiResponse> {
  //   if (this.isSimpleBooking) {
  //     await this.$refs.stdForm.searchEntity();
  //     if (this.hasConflicts) {
  //       this.$store.commit(`${this.storeName}/setShowConflicts', true);
  //       // show Conflicts
  //       return null;
  //     }
  //   }
  //   const res = await this.$refs.stdForm.saveEntity();
  //   return res;
  // }

  private async showSelectResourceModalIfNeccesary() {
    await this.$store.dispatch(`${this.storeName}/calculateResourcesSelectInfo`);

    if (this.resourcesSelectInfo.length > 1 && this.repetitive) {
      this.showResourceSelect = true;
      this.$bvModal.show('modal-select-resource');
    }
  }

  async clickOnSearchButton(): Promise<string> {
    if (this.cantSearchBooking) {
      console.log('cantSaveBooking');
      console.log('messages', this.cantSearchBookingMessages);
      this.showCantSearchBookingMessage = true;
      return null;
    }

    this.showCantSearchBookingMessage = false;

    const selectedResourceIds = JSON.parse(JSON.stringify(this.resourceIds)); // We do a copy becouse the searchEntity() change the resourceIds attr
    await this.$refs.stdForm.searchEntity();
    this.$store.commit(`${this.storeName}/setIsSearched`, true);
    if (!selectedResourceIds || !selectedResourceIds.length) {
      this.showSelectResourceModalIfNeccesary();
    }
    return 'searched';
  }

  fieldsRelatedWithSearchChanged() {
    if (!this.showResourceSelect) {
      this.$store.commit(`${this.storeName}/setIsSearched`, false);
      this.$store.commit(`${this.storeName}/setShowReplaceButton`, false);
      this.$store.commit(`${this.storeName}/setShowConflicts`, false);
    }
  }

  cancelSelectResource() {
    this.$store.commit(`${this.storeName}/setModelProp`, { prop: 'bookingEvents', value: [] });

    this.$nextTick(() => {
      // The whole view is rendered, so I can safely access or query
      // the DOM. ¯\_(ツ)_/¯
      this.$store.commit(`${this.storeName}/setIsSearched`, false);
      this.$bvModal.hide('modal-select-resource');
      this.showResourceSelect = false;
    });
  }

  selectResource(selectedResource: Resource) {
    const resources = this.resources.filter(resource => {
      return resource.id == selectedResource.id;
    });
    // this.$store.commit(`${this.storeName}/setModelProp', { prop: 'resources', resources });
    this.$set(this.$refs.stdForm.formModel, 'resources', resources);

    const bookingEvents = this.bookingEvents.filter(bookingEvent => {
      return bookingEvent.resource.id == selectedResource.id;
    });

    // this.$store.commit(`${this.storeName}/setModelProp', { prop: 'bookingEvents', bookingEvents });
    this.$set(this.$refs.stdForm.formModel, 'bookingEvents', bookingEvents);

    this.$nextTick(() => {
      // The whole view is rendered, so I can safely access or query
      // the DOM. ¯\_(ツ)_/¯
      this.$store.commit(`${this.storeName}/setIsSearched`, true);
      this.$bvModal.hide('modal-select-resource');
      this.showResourceSelect = false;
    });
  }
  // async searchEntity(): Promise<BPCGenericApiResponse> {
  //   const res = await this.$refs.stdForm.searchEntity();
  //   return res;

  // }
  // @Watch('formModel')
  // onFormModelChange() {
  //   this.$emit('input', this.formModel);

  // }

  @Watch('type')
  onTypeChange(val) {
    if (val === 'label') {
      this.$store.commit(`${this.storeName}/setModelProp`, {
        prop: 'generate_order',
        value: false,
      });
    } else {
      // Only change if it is creating a booking
      if (this.currentAction !== 'edit') {
        this.$store.commit(`${this.storeName}/setModelProp`, {
          prop: 'generate_order',
          value: true,
        });
      }
    }
    this.fieldsRelatedWithSearchChanged();
    this.calculatePriceIfNothingChange();
  }
  @Watch('startDate')
  onStartDateChange(val) {
    this.$store.commit(`${this.storeName}/setModelProp`, {
      prop: 'date_range.start_date',
      value: val || '',
    });
    this.fieldsRelatedWithSearchChanged();
    this.calculatePriceIfNothingChange();
  }
  @Watch('startTime')
  onStartTimeChange() {
    this.fieldsRelatedWithSearchChanged();
    this.calculatePriceIfNothingChange();
  }
  @Watch('endDate')
  onEndDateChange(val) {
    this.$store.commit(`${this.storeName}/setModelProp`, {
      prop: 'date_range.end_date',
      value: val || '',
    });
    this.fieldsRelatedWithSearchChanged();
    this.calculatePriceIfNothingChange();
  }
  @Watch('dateRange', { deep: true })
  onDateRangeChange(val: DateRange) {
    this.$store.commit(`${this.storeName}/setModelProp`, {
      prop: 'start_date',
      value: val.start_date,
    });
    this.$store.commit(`${this.storeName}/setModelProp`, { prop: 'end_date', value: val.end_date });
  }
  @Watch('timeRange', { deep: true })
  onTimeRangeChange(val: TimeRange) {
    this.$store.commit(`${this.storeName}/setModelProp`, {
      prop: 'start_time',
      value: val.start_time,
    });
    this.$store.commit(`${this.storeName}/setModelProp`, { prop: 'end_time', value: val.end_time });
  }
  @Watch('endTime')
  onEndTimeChange() {
    this.fieldsRelatedWithSearchChanged();
    this.calculatePriceIfNothingChange();
  }
  @Watch('resourceIds', { deep: true })
  onResourceIdsChange(val, oldVal) {
    // It was changing even with the same oldVal as val
    // Only do things if the value is diferent
    if (val.join(',') !== oldVal.join(',')) {
      this.fieldsRelatedWithSearchChanged();
      this.calculatePriceIfNothingChange();
    }
  }
  @Watch('contactSubscriptionsStr')
  onContactSubscriptionsStrChange() {
    this.fieldsRelatedWithSearchChanged();
  }
  // @Watch('bookingEvents', { deep: true })
  // onBookingEventsChange(val, oldVal) {
  //   console.log('booking event changed', val, oldVal);
  //   this.calculatePriceIfNothingChange();
  // }
  @Watch('contactId')
  onContactChange() {
    this.calculatePriceIfNothingChange();
    if (!this.entityId) {
      this.changePriceCategory();
      this.changeApplyFreeOrders();
    }
  }
  @Watch('priceCategoryId')
  onPriceCategoryChange() {
    this.calculatePriceIfNothingChange();
    // this.changePriceCategory();
  }
  @Watch('disciplineId')
  onDisciplineIdChange() {
    this.resetMultiplayerDataIfNedded();
    this.calculatePriceIfNothingChange();
  }
  @Watch('selectedSuggestedArticles', { deep: true })
  onSelectedSuggestedArticlesChange() {
    this.calculatePriceIfNothingChange();
  }
  @Watch('repetitive')
  onRepetitiveChage() {
    if (!this.entityId) {
      this.setWeekDay();
    }
    this.resetMultiplayerDataIfNedded();
    this.calculatePriceIfNothingChange();
  }
  @Watch('daysStr')
  onDaysStrChange() {
    if (!this.isSimpleBooking) {
      this.fieldsRelatedWithSearchChanged();
    }
  }
  @Watch('reductionArticles')
  reductionArticlesChange() {
    this.calculatePriceIfNothingChange();
  }
  @Watch('article')
  onArticleChange() {
    this.$store.commit(`${this.storeName}/setModelProp`, {
      prop: 'article_id',
      value: this.article?.id,
    });
  }
  @Watch('articleId')
  onArticleIdChange() {
    this.calculatePriceIfNothingChange();
  }

  @Watch('multiplayerBookingData', { deep: true })
  onMultiplayerChange() {
    setProp(
      this.$refs.stdForm.formModel,
      'number_of_players',
      this.multiplayerBookingData.number_of_players,
    );
    setProp(this.$refs.stdForm.formModel, 'players', this.multiplayerBookingData.players);
    setProp(
      this.$refs.stdForm.formModel,
      'is_multiplayer',
      this.isMultiplayerAllowed && !!this.multiplayerBookingData.number_of_players,
    );
  }
  // @Watch('formConfigName', { immediate: true })
  // onFormConfigNameChange(val) {
  //   console.log('onFormConfigNameChange', val);
  //   this.i++;
  // }
}
